import React from "react"
import {useAccessToken} from "../../services/auth"
import {Navigate, useParams} from "react-router-dom"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {
	getProductApplication,
	isBusinessProductApplicationDocument,
	ProductApplication,
	reevaluableProductApplicationStatuses,
} from "../../resources/productApplication"
import {isBeneficialOwner} from "../../resources/beneficialOwner"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import ReevaluateProductApplicationContainer from "./ReevaluateProductApplicationContainer"
import ReevaluateProductApplicationOfficerContainer from "./ReevaluateProductApplicationOfficerContainer"
import ReevaluateProductApplicationBeneficialOwnerContainer from "./ReevaluateProductApplicationBeneficialOwnerContainer"
import {OkDocument} from "../../resources/common"
import {Err} from "../../components/Err/Err"
import {isOfficer} from "../../resources/productApplication"
import {Officer} from "../../resources/application"

export enum ProductApplicationResubmitForms {
	individualForm = "product-application-individual-form",
	businessForm = "product-application-business-form",
	officerForm = "product-application-officer-form",
	beneficialOwnerForm = "product-application-beneficial-owner-form",
}

interface Props {
	children(props: {application: OkDocument<ProductApplication>}): JSX.Element
}

function ReevaluateProductApplicationPageWrapper({children}: Props) {
	const {productApplicationId = ""} = useParams<{productApplicationId: string}>()
	const accessToken = useAccessToken()
	const applicationAsyncResult = useAsyncResult(() => getProductApplication(accessToken, productApplicationId))

	return (
		<HasPermission resource="productApplication" action="update">
			<AsyncResultComponent asyncResult={applicationAsyncResult}>
				{({value: application}) => {
					if (!reevaluableProductApplicationStatuses.includes(application.data.attributes.status)) {
						return <Navigate to={`/product-applications/${application.data.id}`} replace />
					}
					return <React.Fragment>{children({application})}</React.Fragment>
				}}
			</AsyncResultComponent>
		</HasPermission>
	)
}

export function ReevaluateProductApplicationPage() {
	return (
		<ReevaluateProductApplicationPageWrapper>
			{({application}) => {
				return <ReevaluateProductApplicationContainer application={application} />
			}}
		</ReevaluateProductApplicationPageWrapper>
	)
}

export function ReevaluateProductApplicationOfficerPage() {
	return (
		<ReevaluateProductApplicationPageWrapper>
			{({application}) => {
				if (!isBusinessProductApplicationDocument(application)) {
					return <Err err={"Provided Product Application Should Be Business Product Application"} />
				}

				const officerResource = application.included?.find(
					(r) => isOfficer(r) && r.id === application.data.relationships.officer?.data.id
				)
				const officer = officerResource?.attributes as Officer | undefined

				if (!officer || !application.data.relationships.officer) {
					return <Err err={"Provided Product Application Should Have Officer"} />
				}

				return (
					<ReevaluateProductApplicationOfficerContainer
						application={application}
						officer={officer}
						officerId={application.data.relationships.officer.data.id}
					/>
				)
			}}
		</ReevaluateProductApplicationPageWrapper>
	)
}

export function ReevaluateProductApplicationBeneficialOwnerPage() {
	const {beneficialOwnerId = ""} = useParams<{beneficialOwnerId: string}>()

	return (
		<ReevaluateProductApplicationPageWrapper>
			{({application}) => {
				if (!isBusinessProductApplicationDocument(application)) {
					return <Err err="Provided Product Application Should Be Business Product Application" />
				}
				const beneficialOwnerResource = application.included?.find(
					(e) => e.type === "beneficialOwner" && e.id === beneficialOwnerId
				)

				if (!beneficialOwnerResource || !isBeneficialOwner(beneficialOwnerResource)) {
					return <Err err="Provided Beneficial Owner is incorrect" />
				}

				return (
					<ReevaluateProductApplicationBeneficialOwnerContainer
						application={application}
						beneficialOwnerResource={beneficialOwnerResource}
					/>
				)
			}}
		</ReevaluateProductApplicationPageWrapper>
	)
}
